import { JSX, useState } from "react";

import MenuIcon from "@mui/icons-material/Menu";
import { Box, CssBaseline, IconButton, Toolbar, Typography } from "@mui/material";
import MuiAppBar, { type AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import { styled, Theme, useTheme } from "@mui/material/styles";
import images from "src/constants";

import SidebarMenu from "../SidebarContent";

const drawerWidth = 250;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  backgroundColor: "white",
  color: theme.palette.text.primary,
  height: 72,
  border: "none",
}));

const Drawer = styled(MuiDrawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    display: "flex",
    alignItems: "flex-start",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: theme.palette.transparent.pure,
  },
}));

const getStyles = (theme: Theme) => ({
  toolbar: {
    border: "none",
    display: "flex",
    px: 2.5,
    py: 2.5,
    alignItems: "center",
    gap: 1.375,
    height: 72,
  },
  iconButton: (open: boolean) => ({ width: 32, height: 32, display: open ? "none" : "button" }),
  sidebarContent: {
    width: drawerWidth,
    height: "100vh",
    backgroundColor: "white",
    boxShadow: `2px 0px 5px ${theme.palette.border.strong}`,
  },
  drawer: {
    border: "none",
    "& .MuiDrawer-paper": {
      border: "none",
      borderRight: `1px solid ${theme.palette.border.default}`,
    },
  },
});

function MobileSidebar(): JSX.Element {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  return (
    <Box display='flex'>
      <CssBaseline />
      <AppBar position='fixed' open={open}>
        <Toolbar sx={getStyles(theme).toolbar}>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            onClick={toggleDrawer(true)}
            edge='start'
            sx={getStyles(theme).iconButton(open)}
          >
            <MenuIcon />
          </IconButton>
          <Box>
            <img width={32} height={32} src={images.logo} alt='DiamondApp' />
          </Box>
          <Typography lineHeight={1} fontSize={theme.fontSizes.lg} fontWeight={theme.typography.fontWeightRegular}>
            Diamond App
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer variant='temporary' open={open} onClose={toggleDrawer(false)} sx={getStyles(theme).drawer}>
        <Box
          className='drawer-content'
          sx={getStyles(theme).sidebarContent}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SidebarMenu open={open} />
        </Box>
      </Drawer>
    </Box>
  );
}

export default MobileSidebar;
